import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "../../api/client";
import type { SiteNewsItem } from "../../types/api";
import { fallbackNews } from "../aan-site/newsData";

function formatNewsDate(value: string) {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export default function NewsDetailPage() {
  const { slug } = useParams();
  const [items, setItems] = useState<SiteNewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let active = true;

    setIsLoading(true);
    publicApi
      .getSiteContent()
      .then(({ content }) => {
        if (active) setItems(content.news);
      })
      .catch(() => {
        if (active) setItems(fallbackNews);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const item = useMemo(
    () => items.find((candidate) => candidate.slug === slug && candidate.published),
    [items, slug]
  );
  const body = item?.body || item?.excerpt || "";
  const paragraphs = body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (isLoading) {
    return (
      <main className="aan-site flex min-h-screen items-center justify-center bg-black px-6 text-white">
        <div className="font-display text-sm font-semibold uppercase tracking-[0.24em] text-white/60">
          Loading news...
        </div>
      </main>
    );
  }

  if (!item) {
    return (
      <main className="aan-site flex min-h-screen items-center justify-center bg-black px-6 text-white">
        <div className="max-w-xl text-center">
          <div className="font-display text-xs font-black uppercase tracking-[0.24em] text-aan-bright">News</div>
          <h1 className="mt-4 font-display text-4xl font-black uppercase leading-none text-white">
            Article not found
          </h1>
          <p className="mx-auto mt-5 max-w-md text-sm leading-6 text-white/70">
            This article may be unpublished or no longer available.
          </p>
          <Link
            className="mt-8 inline-flex min-h-11 items-center justify-center rounded-full bg-aan-bright px-6 font-display text-xs font-black uppercase tracking-[0.14em] text-black transition hover:bg-white"
            to="/#news"
          >
            Back to news
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="aan-site min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black px-6 py-5">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <Link to="/" aria-label="African Art Network home">
            <img className="w-32 sm:w-44" src="/assets/logo-white.png" alt="African Art Network" />
          </Link>
          <Link
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/20 px-4 font-display text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:border-aan-bright hover:text-aan-bright"
            to="/#news"
          >
            <ArrowLeft size={14} />
            News
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-5xl px-6 py-12 sm:py-16 lg:py-20">
        {item.image ? (
          <img
            className="aspect-[16/9] w-full rounded-[8px] object-cover"
            src={item.image}
            alt={item.imageAlt || item.title}
          />
        ) : null}

        <div className="mt-10 max-w-3xl">
          <div className="font-display text-xs font-black uppercase tracking-[0.24em] text-aan-bright">
            {[item.author, formatNewsDate(item.publishedAt)].filter(Boolean).join(" / ")}
          </div>
          <h1 className="mt-5 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
            {item.title}
          </h1>
          {item.excerpt ? <p className="mt-7 text-lg leading-8 text-white/72">{item.excerpt}</p> : null}
        </div>

        <div className="mt-10 max-w-3xl space-y-6 text-base leading-8 text-white/82">
          {paragraphs.length > 0 ? (
            paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
          ) : (
            <p>Full article details will be published soon.</p>
          )}
        </div>
      </article>
    </main>
  );
}
